import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Plus,
	Pencil,
	Trash2,
	Plug,
	Route as RouteIcon,
	BarChart3,
	CheckCircle2,
	XCircle,
	RefreshCw,
	Loader2,
	Server,
	Cloud,
	Bot,
	Wrench,
	KeyRound,
	FlaskConical,
	ListChecks,
	Sparkles,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import AbaFerramentas from "./AbaFerramentas";
import AbaCredenciais from "./AbaCredenciais";
import AbaSimulador from "./AbaSimulador";
import AbaCenarios from "./AbaCenarios";
import AbaAssistente from "./AbaAssistente";

const ESTRATEGIAS = [
	{ value: "priority", rotulo: "Ordem que eu definir" },
	{ value: "speed", rotulo: "O mais rápido" },
	{ value: "cost", rotulo: "O mais barato" },
	{ value: "availability", rotulo: "O que está no ar" },
	{ value: "roundRobin", rotulo: "Alternar entre eles" },
];

const AiHub = () => {
	// A tela abre nos provedores, como sempre: o assistente é a primeira aba da
	// barra, mas depende do módulo de agentes — abrir nele mostraria "módulo
	// não contratado" para quem não tem.
	const [aba, setAba] = useState("provedores");
	const [providers, setProviders] = useState([]);
	const [disponiveis, setDisponiveis] = useState([]);
	const [rotas, setRotas] = useState([]);
	const [agentes, setAgentes] = useState([]);
	const [filas, setFilas] = useState([]);
	const [ferramentas, setFerramentas] = useState([]);
	const [credenciais, setCredenciais] = useState([]);
	const [avisoModulo, setAvisoModulo] = useState(null);
	const [consumo, setConsumo] = useState(null);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);
	// A conversa do assistente mora aqui para sobreviver à troca de aba: o
	// botão "Abrir no Simulador" joga a pessoa para outro lugar e a
	// conversa continua onde parou.
	const [assistente, setAssistente] = useState(null);

	const [modal, setModal] = useState(null);
	const [form, setForm] = useState({});

	// Modelos oferecidos pelo provedor do formulário aberto.
	const [modelos, setModelos] = useState([]);
	const [buscandoModelos, setBuscandoModelos] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);

	const carregar = useCallback(async () => {
		try {
			const [lista, cat, r, u, ag] = await Promise.all([
				api.get("/ai/providers"),
				api.get("/ai/available-providers"),
				api.get("/ai/routes"),
				api.get("/ai/usage"),
				api.get("/ai/agents"),
			]);
			setProviders(lista.data);
			setDisponiveis(cat.data);
			setRotas(r.data);
			setConsumo(u.data);
			setAgentes(ag.data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	// Filas da empresa: o agente pode passar a conversa para outra fila quando
	// não resolve o que o cliente pediu.
	useEffect(() => {
		api
			.get("/queue")
			.then(({ data }) => setFilas(Array.isArray(data) ? data : []))
			.catch(() => setFilas([]));
	}, []);

	// Ferramentas e credenciais vivem no módulo de agentes de IA. Empresa sem
	// o módulo não tem o que ver aqui: em vez de quebrar a tela, a aba avisa
	// que o recurso não está contratado.
	const carregarFerramentas = useCallback(async () => {
		try {
			const [f, c] = await Promise.all([
				api.get("/ai/tools"),
				api.get("/ai/credentials"),
			]);
			setFerramentas(Array.isArray(f.data) ? f.data : []);
			setCredenciais(Array.isArray(c.data) ? c.data : []);
			setAvisoModulo(null);
		} catch (err) {
			setFerramentas([]);
			setCredenciais([]);

			const mensagem =
				err?.response?.data?.error || err?.response?.data?.message || "";

			if (
				err?.response?.status === 403 &&
				mensagem.includes("ERR_MODULE_NOT_CONTRACTED")
			) {
				setAvisoModulo("Módulo Agentes de IA não contratado");
				return;
			}

			toastError(err);
		}
	}, []);

	// Conexão já salva tem chave no servidor: dá para trazer a lista assim que o
	// formulário abre, sem a pessoa precisar pedir. Em cadastro novo a lista só
	// existe depois da chave, então fica vazia até o botão ser clicado.
	useEffect(() => {
		if (modal !== "provedor") {
			setModelos([]);
			return;
		}

		if (form.id) listarModelos({ silencioso: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal, form.id]);

	const salvarProvedor = async () => {
		setSalvando(true);
		try {
			const corpo = {
				name: form.name,
				apiKey: form.apiKey || undefined,
				baseUrl: form.baseUrl || undefined,
				defaultModel: form.defaultModel || undefined,
				priority: Number(form.priority) || 100,
			};

			if (form.id) {
				await api.put(`/ai/providers/${form.id}`, corpo);
			} else {
				await api.post("/ai/providers", { ...corpo, provider: form.provider });
			}

			toast.success("Provedor salvo.");
			setModal(null);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const testar = async (provider) => {
		setSalvando(true);
		try {
			const { data } = await api.post(`/ai/providers/${provider.id}/test`);
			if (data.ok) {
				toast.success(`${provider.name} respondeu em ${data.latencyMs}ms.`);
			} else {
				toast.error(`${provider.name}: ${data.error}`);
			}
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	// Modelos que o provedor oferece agora, para o campo "modelo padrão" ser uma
	// escolha e não um nome digitado de cabeça. Funciona antes de salvar: a
	// chave recém-colada vai junto na consulta.
	const listarModelos = async ({ silencioso } = {}) => {
		if (!form.provider && !form.id) return;

		setBuscandoModelos(true);
		try {
			const { data } = await api.post("/ai/providers/available-models", {
				providerId: form.id,
				provider: form.provider,
				apiKey: form.apiKey || undefined,
				baseUrl: form.baseUrl || undefined,
			});

			setModelos(data || []);

			if (!silencioso) {
				toast.success(`${(data || []).length} modelo(s) disponível(is).`);
			}
		} catch (err) {
			setModelos([]);
			// No carregamento automático o erro é esperado (chave ainda não
			// colada); avisar ali seria barulho a cada abertura do formulário.
			if (!silencioso) toastError(err);
		}
		setBuscandoModelos(false);
	};

	const buscarModelos = async (provider) => {
		setSalvando(true);
		try {
			const { data } = await api.post(
				`/ai/providers/${provider.id}/sync-models`
			);
			toast.success(`${data.length} modelo(s) encontrado(s).`);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/ai/providers/${selecionado.id}`);
			toast.success("Provedor removido.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	const salvarAgente = async () => {
		setSalvando(true);
		try {
			const corpo = {
				name: form.name,
				prompt: form.prompt,
				aiProviderId: form.aiProviderId ? Number(form.aiProviderId) : null,
				model: form.model || null,
				temperature: Number(form.temperature ?? 0.7),
				maxMessages: Number(form.maxMessages ?? 5),
				isActive: form.isActive !== false,
				handoffMessage: form.handoffMessage?.trim() || null,
				// A assinatura abre a mensagem do agente em negrito, como a
				// dos atendentes. Em branco, ele fala sem se identificar.
				signature: form.signature?.trim() || null,
				handoffQueueId: form.handoffQueueId ? Number(form.handoffQueueId) : null,
				historyLimit: Number(form.historyLimit ?? 20),
				// As ferramentas que este agente pode chamar. Lista vazia é
				// "nenhuma": o agente responde só com o texto.
				toolIds: (form.toolIds || []).map((id) => Number(id)),
			};

			if (form.id) {
				await api.put(`/ai/agents/${form.id}`, corpo);
			} else {
				await api.post("/ai/agents", corpo);
			}

			toast.success("Agente salvo.");
			setModal(null);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluirAgente = async () => {
		try {
			await api.delete(`/ai/agents/${selecionado.id}`);
			toast.success("Agente removido.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	const salvarRota = async () => {
		setSalvando(true);
		try {
			await api.put(`/ai/routes/${form.feature}`, {
				aiProviderId: form.aiProviderId ? Number(form.aiProviderId) : null,
				model: form.model || null,
				strategy: form.strategy,
				fallbackChain: form.fallbackChain || [],
			});
			toast.success("Roteamento salvo.");
			setModal(null);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const definicao = (chave) => disponiveis.find((d) => d.key === chave);

	// A lista de agentes é recarregada quando a aba que a usa é aberta: o
	// assistente cria agente do lado dele e, sem isto, as outras abas
	// continuavam vendo a lista antiga até o próximo F5.
	const carregarAgentes = useCallback(async () => {
		try {
			const { data } = await api.get("/ai/agents");
			setAgentes(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	// A aba das ferramentas e a das credenciais carregam os dados na hora em
	// que são abertas: quem nunca entra nelas não paga a requisição.
	const trocarAba = (nova) => {
		setAba(nova);

		if (nova === "ferramentas" || nova === "credenciais") {
			carregarFerramentas();
		}

		// Quem mostra a lista de agentes (a aba Agentes, o Simulador e os
		// Cenários) precisa dela atualizada: o assistente cria agente sem
		// passar por aqui.
		if (
			nova === "agentes" ||
			nova === "simulador" ||
			nova === "cenarios"
		) {
			carregarAgentes();
		}
	};

	// Só a ferramenta ativa pode ser marcada: rascunho não entra na lista.
	const ferramentasAtivas = ferramentas.filter(
		(ferramenta) => ferramenta.status === "active"
	);

	// O formulário do agente abre com as ferramentas carregadas: quem marca
	// uma ferramenta precisa ver a lista antes de marcar.
	const abrirAgente = (agente) => {
		if (agente) {
			setForm({
				...agente,
				handoffQueueId: agente.handoffQueueId
					? String(agente.handoffQueueId)
					: "",
				handoffMessage: agente.handoffMessage || "",
				signature: agente.signature || "",
				toolIds: (agente.tools || []).map((ferramenta) => ferramenta.id),
			});
		} else {
			setForm({
				name: "",
				prompt: "",
				temperature: 0.7,
				maxMessages: 5,
				isActive: true,
				historyLimit: 20,
				handoffMessage: "",
				signature: "",
				handoffQueueId: "",
				toolIds: [],
			});
		}

		carregarFerramentas();
		setModal("agente");
	};

	const alternarFerramenta = (id, marcada) => {
		const ids = form.toolIds || [];

		setForm({
			...form,
			toolIds: marcada
				? [...ids, id]
				: ids.filter((ferramenta) => ferramenta !== id),
		});
	};

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Remover ${selecionado.name}?`}
				open={Boolean(confirmOpen)}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={confirmOpen === "agente" ? excluirAgente : excluir}
			>
				{confirmOpen === "agente"
					? "As conexões que usavam este agente param de responder com IA até você escolher outro."
					: "As funcionalidades que usavam este provedor passam a usar outro."}
			</ConfirmationModal>

			<div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						Inteligência Artificial
					</h1>
					<p className="text-xs text-muted-foreground">
						{aba === "assistente"
							? "Descreva em português o agente que você quer montar"
							: "Conecte um ou mais provedores e escolha quem atende cada recurso"}
					</p>
				</div>
				{aba === "provedores" && (
					<Button
						className="h-9"
						onClick={() => {
							setForm({ provider: "", name: "", priority: 100 });
							setModal("provedor");
						}}
					>
						<Plus className="h-4 w-4" />
						<span className="hidden sm:inline">Conectar IA</span>
						<span className="sm:hidden">Conectar</span>
					</Button>
				)}
			{aba === "agentes" && (
				<Button
					className="h-9"
					onClick={() => abrirAgente()}
				>
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">Novo agente</span>
					<span className="sm:hidden">Novo</span>
				</Button>
			)}
		</div>

		<PageTabs
			abas={[
				{
					value: "assistente",
					rotulo: "Assistente",
					icone: Sparkles,
				},
				{
					value: "provedores",
					rotulo: "Provedores",
					icone: Plug,
					contador: providers.length,
				},
				{
					value: "agentes",
					rotulo: "Agentes",
					icone: Bot,
					contador: agentes.length,
				},
				{
					value: "simulador",
					rotulo: "Simulador",
					icone: FlaskConical,
				},
				{
					value: "cenarios",
					rotulo: "Cenários",
					icone: ListChecks,
				},
				{
					value: "ferramentas",
					rotulo: "Ferramentas",
					icone: Wrench,
					contador: ferramentas.length,
				},
				{
					value: "credenciais",
					rotulo: "Credenciais",
					icone: KeyRound,
					contador: credenciais.length,
				},
				{ value: "rotas", rotulo: "Quem faz o quê", icone: RouteIcon },
				{ value: "consumo", rotulo: "Consumo", icone: BarChart3 },
			]}
			atual={aba}
			onChange={trocarAba}
		/>


			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{/* ---------------- assistente ---------------- */}
				{aba === "assistente" && (
					<AbaAssistente
						dados={assistente}
						aoMudar={setAssistente}
						onIrPara={trocarAba}
					/>
				)}

				{/* ---------------- provedores ---------------- */}
				{aba === "provedores" &&
					(providers.length === 0 ? (
						<div className="rounded-xl border bg-card py-16 text-center">
							<Plug className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-3 text-sm font-medium">Nenhuma IA conectada</p>
							<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
								Conecte pelo menos um provedor para usar o copiloto, os resumos
								automáticos e a análise das conversas.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{providers.map((provider) => {
								const def = definicao(provider.provider);
								return (
									<div
										key={provider.id}
										className="flex flex-col rounded-xl border bg-card p-4"
									>
										<div className="flex items-start justify-between gap-2">
											<div className="flex min-w-0 items-start gap-2.5">
												<span
													className={cn(
														"mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
														provider.isDown
															? "bg-destructive/10 text-destructive"
															: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
													)}
												>
													{def?.local ? (
														<Server className="h-4 w-4" />
													) : (
														<Cloud className="h-4 w-4" />
													)}
												</span>
												<div className="min-w-0">
													<p className="truncate font-medium">
														{provider.name}
													</p>
													<p className="truncate text-xs text-muted-foreground">
														{def?.name || provider.provider}
													</p>
												</div>
											</div>

											<span
												className={cn(
													"flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
													provider.isDown
														? "bg-destructive/10 text-destructive"
														: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
												)}
											>
												{provider.isDown ? (
													<XCircle className="h-3 w-3" />
												) : (
													<CheckCircle2 className="h-3 w-3" />
												)}
												{provider.isDown ? "Fora do ar" : "No ar"}
											</span>
										</div>

										<dl className="mt-3 space-y-1 text-xs">
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Modelo padrão</dt>
												<dd className="truncate font-medium">
													{provider.defaultModel || "—"}
												</dd>
											</div>
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Modelos</dt>
												<dd className="font-medium">
													{provider.models?.length || 0}
												</dd>
											</div>
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Ordem</dt>
												<dd className="font-medium tabular-nums">
													{provider.priority}
												</dd>
											</div>
										</dl>

										{provider.isDown && provider.lastError && (
											<p className="mt-2 rounded-md bg-destructive/5 p-2 text-[11px] leading-relaxed text-destructive">
												{provider.lastError}
											</p>
										)}

										<div className="mt-3 flex flex-wrap gap-1 border-t pt-3">
											<Button
												variant="outline"
												size="sm"
												className="h-7 text-xs"
												disabled={salvando}
												onClick={() => testar(provider)}
											>
												Testar
											</Button>
											<Button
												variant="outline"
												size="sm"
												className="h-7 text-xs"
												disabled={salvando}
												onClick={() => buscarModelos(provider)}
											>
												<RefreshCw className="h-3 w-3" />
												Modelos
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="ml-auto h-7 w-7"
												aria-label={`Editar ${provider.name}`}
												onClick={() => {
													setForm({
														id: provider.id,
														provider: provider.provider,
														name: provider.name,
														baseUrl: provider.baseUrl || "",
														defaultModel: provider.defaultModel || "",
														priority: provider.priority,
														apiKey: "",
														hasApiKey: provider.hasApiKey,
													});
													setModal("provedor");
												}}
											>
												<Pencil className="h-3.5 w-3.5" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												aria-label={`Remover ${provider.name}`}
												onClick={() => {
													setSelecionado(provider);
													setConfirmOpen("provedor");
												}}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										</div>
									</div>
								);
							})}
						</div>
					))}

				{/* ---------------- roteamento ---------------- */}
				{/* ---------------- agentes ---------------- */}
				{aba === "agentes" &&
					(agentes.length === 0 ? (
						<div className="rounded-xl border bg-card py-16 text-center">
							<Bot className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-3 text-sm font-medium">Nenhum agente criado</p>
							<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
								Um agente é a instrução que a IA segue ao responder no seu
								lugar. Crie um aqui e escolha-o na conexão, em Conexões ›
								editar › Atendimento.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{agentes.map((agente) => (
								<div
									key={agente.id}
									className="flex flex-col rounded-xl border bg-card p-4"
								>
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<p className="truncate text-sm font-medium">
												{agente.name}
											</p>
											<p className="text-xs text-muted-foreground">
												{agente.provider?.name || "Qualquer provedor"}
												{agente.model ? ` · ${agente.model}` : ""}
											</p>
										</div>
										{agente.isActive ? (
											<CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
										) : (
											<XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
										)}
									</div>

									<p className="mt-3 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
										{agente.prompt}
									</p>

									<div className="mt-3 flex items-center gap-1.5 border-t pt-3">
										<span className="text-xs text-muted-foreground">
											Até {agente.maxMessages || "∞"} resposta(s) por conversa
											{agente.tools?.length
												? ` · ${agente.tools.length} ferramenta(s)`
												: ""}
											{agente.signature ? ` · assina como ${agente.signature}` : ""}
										</span>
										<Button
											variant="ghost"
											size="icon"
											className="ml-auto h-7 w-7"
											aria-label={`Editar ${agente.name}`}
											onClick={() => abrirAgente(agente)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-destructive"
											aria-label={`Remover ${agente.name}`}
											onClick={() => {
												setSelecionado(agente);
												setConfirmOpen("agente");
											}}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							))}
						</div>
					))}

				{/* ---------------- simulador ---------------- */}
				{aba === "simulador" && <AbaSimulador agentes={agentes} />}

				{/* ---------------- cenários ---------------- */}
				{aba === "cenarios" && <AbaCenarios agentes={agentes} />}

				{/* ---------------- ferramentas ---------------- */}
				{aba === "ferramentas" && (
					<AbaFerramentas
						ferramentas={ferramentas}
						credenciais={credenciais}
						aoMudar={carregarFerramentas}
						aviso={avisoModulo}
					/>
				)}

				{/* ---------------- credenciais ---------------- */}
				{aba === "credenciais" && (
					<AbaCredenciais
						credenciais={credenciais}
						aoMudar={carregarFerramentas}
						aviso={avisoModulo}
					/>
				)}

				{aba === "rotas" && (
					<div className="space-y-2">
						<p className="mb-3 text-xs leading-relaxed text-muted-foreground">
							Escolha qual IA atende cada recurso. Se ela falhar, o sistema tenta
							a próxima da lista automaticamente.
						</p>

						{rotas.map((rota) => (
							<div
								key={rota.feature}
								className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3"
							>
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-medium">{rota.name}</p>
									<p className="truncate text-xs text-muted-foreground">
										{rota.providerName || "Nenhuma escolhida — usa a primeira disponível"}
										{rota.fallbackChain?.length > 0 &&
											` · ${rota.fallbackChain.length} reserva(s)`}
									</p>
								</div>
								<Button
									variant="outline"
									size="sm"
									className="h-8 shrink-0"
									disabled={providers.length === 0}
									onClick={() => {
										setForm({
											feature: rota.feature,
											name: rota.name,
											aiProviderId: rota.aiProviderId
												? String(rota.aiProviderId)
												: "",
											model: rota.model || "",
											strategy: rota.strategy || "priority",
											fallbackChain: rota.fallbackChain || [],
										});
										setModal("rota");
									}}
								>
									Configurar
								</Button>
							</div>
						))}
					</div>
				)}

				{/* ---------------- consumo ---------------- */}
				{aba === "consumo" && consumo && (
					<div className="space-y-4">
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
							{[
								{ rotulo: "Chamadas", valor: consumo.total.chamadas },
								{
									rotulo: "Tokens",
									valor: consumo.total.tokens.toLocaleString("pt-BR"),
								},
								{
									rotulo: "Custo",
									valor: `US$ ${consumo.total.custo.toFixed(4)}`,
								},
								{
									rotulo: "Salvas por reserva",
									valor: consumo.total.salvasPorFailover,
								},
							].map((item) => (
								<div key={item.rotulo} className="rounded-xl border bg-card p-4">
									<p className="text-xs text-muted-foreground">{item.rotulo}</p>
									<p className="mt-1 text-2xl font-semibold tabular-nums">
										{item.valor}
									</p>
								</div>
							))}
						</div>

						{[
							{ titulo: "Por provedor", dados: consumo.porProvedor },
							{ titulo: "Por recurso", dados: consumo.porFuncionalidade },
							{ titulo: "Por usuário", dados: consumo.porUsuario },
						].map((bloco) => (
							<section key={bloco.titulo} className="rounded-xl border bg-card">
								<header className="border-b p-3 px-4">
									<h3 className="text-sm font-semibold">{bloco.titulo}</h3>
								</header>
								{bloco.dados.length === 0 ? (
									<p className="py-6 text-center text-xs text-muted-foreground">
										Nada registrado ainda.
									</p>
								) : (
									<ul className="divide-y">
										{bloco.dados.slice(0, 6).map((linha) => (
											<li
												key={linha.chave}
												className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm"
											>
												<span className="min-w-0 flex-1 truncate">
													{linha.chave}
												</span>
												<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
													{linha.chamadas} chamada(s)
												</span>
												{linha.latenciaMedia > 0 && (
													<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
														{linha.latenciaMedia}ms
													</span>
												)}
												<span
													className={cn(
														"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
														linha.sucessoPercent >= 90
															? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
															: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
													)}
												>
													{linha.sucessoPercent}% ok
												</span>
											</li>
										))}
									</ul>
								)}
							</section>
						))}
					</div>
				)}
			</div>

			{/* ================= modal do provedor ================= */}
			<Dialog open={modal === "agente"} onOpenChange={() => setModal(null)}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar agente" : "Novo agente"}
						</DialogTitle>
						<DialogDescription>
							A instrução é o que a IA segue ao responder no lugar do
							atendente. Depois é só escolher este agente na conexão.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label htmlFor="agente-nome">Nome</Label>
							<Input
								id="agente-nome"
								value={form.name || ""}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder="Atendente da loja"
							/>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="agente-prompt">Instrução</Label>
							<textarea
								id="agente-prompt"
								rows={5}
								className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
								value={form.prompt || ""}
								onChange={(e) => setForm({ ...form, prompt: e.target.value })}
								placeholder="Você atende a loja X. Responda curto, em português, e nunca prometa prazo de entrega."
							/>
							<p className="text-xs text-muted-foreground">
								Diga o que ele é, como deve falar e o que não pode fazer.
							</p>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="agente-assinatura">Assinatura (alias)</Label>
							<Input
								id="agente-assinatura"
								maxLength={80}
								value={form.signature || ""}
								onChange={(e) =>
									setForm({ ...form, signature: e.target.value })
								}
								placeholder="Miguel - Suporte"
							/>
							<p className="text-xs text-muted-foreground">
								Aparece em negrito no topo de cada mensagem, como a dos
								atendentes. Ex.: Miguel - Suporte. Vazio = sem
								assinatura.
							</p>
						</div>

						<div className="grid gap-3 sm:grid-cols-2">
							<div className="space-y-1.5">
								<Label>Provedor</Label>
								<Select
									value={form.aiProviderId ? String(form.aiProviderId) : "auto"}
									onValueChange={(v) =>
										setForm({
											...form,
											aiProviderId: v === "auto" ? null : Number(v),
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="auto">Qualquer um (ordem de prioridade)</SelectItem>
										{providers.map((p) => (
											<SelectItem key={p.id} value={String(p.id)}>
												{p.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-modelo">Modelo</Label>
								<Input
									id="agente-modelo"
									value={form.model || ""}
									onChange={(e) => setForm({ ...form, model: e.target.value })}
									placeholder="deixe vazio para o padrão"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-temp">Criatividade</Label>
								<Input
									id="agente-temp"
									type="number"
									step="0.1"
									min="0"
									max="2"
									value={form.temperature ?? 0.7}
									onChange={(e) =>
										setForm({ ...form, temperature: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									0 = sempre igual · 1 = mais variado
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-max">Respostas por conversa</Label>
								<Input
									id="agente-max"
									type="number"
									min="0"
									value={form.maxMessages ?? 5}
									onChange={(e) =>
										setForm({ ...form, maxMessages: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									Atingido o limite, a conversa espera um humano. 0 = sem
									limite.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="agente-historico">Mensagens que a IA lê</Label>
								<Input
									id="agente-historico"
									type="number"
									min="1"
									max="50"
									value={form.historyLimit ?? 20}
									onChange={(e) =>
										setForm({ ...form, historyLimit: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									Quantas mensagens da conversa ela considera antes de
									responder.
								</p>
							</div>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="agente-passagem">Ao passar para um atendente</Label>
							<Textarea
								id="agente-passagem"
								rows={2}
								placeholder="Vou te passar para um atendente, só um instante."
								value={form.handoffMessage ?? ""}
								onChange={(e) =>
									setForm({ ...form, handoffMessage: e.target.value })
								}
							/>
							<p className="text-xs text-muted-foreground">
								Enviada quando o limite de respostas acaba ou o cliente pede
								uma pessoa. Em branco, usa o texto de exemplo.
							</p>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="agente-fila-destino">Transferir para</Label>
							<Select
								value={form.handoffQueueId ? String(form.handoffQueueId) : "mesma"}
								onValueChange={(v) =>
									setForm({ ...form, handoffQueueId: v === "mesma" ? "" : v })
								}
							>
								<SelectTrigger id="agente-fila-destino">
									<SelectValue />
								</SelectTrigger>
							<SelectContent>
								<SelectItem value="mesma">Continua na mesma fila</SelectItem>
								{filas.map((fila) => (
									<SelectItem key={fila.id} value={String(fila.id)}>
										{fila.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-1.5">
						<Label>Ferramentas que este agente pode usar</Label>
						{ferramentasAtivas.length === 0 ? (
							<p className="text-xs leading-relaxed text-muted-foreground">
								Nenhuma ferramenta ativa. Crie uma na aba Ferramentas para o
								agente poder consultar uma API de fora ou agir no
								atendimento.
							</p>
						) : (
							<div className="space-y-1 rounded-lg border p-2">
								{ferramentasAtivas.map((ferramenta) => (
									<label
										key={ferramenta.id}
										className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
									>
										<Checkbox
											checked={(form.toolIds || []).includes(
												ferramenta.id
											)}
											onCheckedChange={(marcado) =>
												alternarFerramenta(
													ferramenta.id,
													Boolean(marcado)
												)
											}
										/>
										<span className="min-w-0 flex-1 truncate">
											{ferramenta.name}
										</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{ferramenta.kind === "http"
												? "API de fora"
												: "Ação interna"}
										</span>
									</label>
								))}
							</div>
						)}
						<p className="text-xs text-muted-foreground">
							A IA só chama o que estiver marcado aqui. Sem marcação, o
							agente responde só com o texto.
						</p>
					</div>
				</div>


					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(null)}>
							Cancelar
						</Button>
						<Button onClick={salvarAgente} disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							{form.id ? "Salvar" : "Criar agente"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={modal === "provedor"} onOpenChange={() => setModal(null)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar conexão" : "Conectar uma IA"}
						</DialogTitle>
						<DialogDescription>
							Você pode conectar várias e usar cada uma para um recurso.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						{!form.id && (
							<div className="space-y-1.5">
								<Label>Qual IA</Label>
								<Select
									value={form.provider}
									onValueChange={(v) => {
										const d = definicao(v);
										// Outra IA, outros modelos: a lista da anterior não vale mais e
										// deixá-la na tela ofereceria nome que este provedor não conhece.
										setModelos([]);
										setForm({
											...form,
											provider: v,
											name: d?.name || "",
											baseUrl: d?.local ? d.baseUrl : "",
											defaultModel: d?.defaultModel || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione" />
									</SelectTrigger>
									<SelectContent>
										{disponiveis.map((d) => (
											<SelectItem key={d.key} value={d.key}>
												{d.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{definicao(form.provider)?.docsUrl && (
									<p className="text-xs text-muted-foreground">
										Pegue a chave em{" "}
										<a
											href={definicao(form.provider).docsUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary underline underline-offset-2"
										>
											{definicao(form.provider).docsUrl.replace(
												/^https?:\/\//,
												""
											)}
										</a>
									</p>
								)}
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="nomeProvedor">Nome desta conexão</Label>
							<Input
								id="nomeProvedor"
								placeholder="Ex.: OpenAI principal"
								value={form.name || ""}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
							/>
						</div>

						{definicao(form.provider)?.requiresApiKey !== false && (
							<div className="space-y-1.5">
								<Label htmlFor="chaveProvedor">Chave de API</Label>
								<Input
									id="chaveProvedor"
									type="password"
									placeholder={
										form.hasApiKey
											? "Deixe vazio para manter a chave atual"
											: "Cole a chave aqui"
									}
									value={form.apiKey || ""}
									onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
								/>
								<p className="text-xs text-muted-foreground">
									A chave fica guardada no servidor e nunca é exibida de volta.
								</p>
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="urlProvedor">Endereço do servidor</Label>
							<Input
								id="urlProvedor"
								placeholder="Deixe vazio para usar o endereço oficial"
								value={form.baseUrl || ""}
								onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
							/>
						</div>

						<div className="grid gap-3 sm:grid-cols-2">
							<div className="space-y-1.5">
								<Label htmlFor="modeloProvedor">Modelo padrão</Label>

								{modelos.length > 0 ? (
									<Select
										value={form.defaultModel || ""}
										onValueChange={(v) =>
											setForm({ ...form, defaultModel: v })
										}
									>
										<SelectTrigger id="modeloProvedor">
											<SelectValue placeholder="Escolha o modelo" />
										</SelectTrigger>
										<SelectContent>
											{/* O que já estava salvo continua na lista mesmo que o
											    provedor tenha parado de anunciá-lo: sem isso o
											    campo apareceria vazio e a conexão seria salva sem
											    modelo nenhum. */}
											{(modelos.some((m) => m.id === form.defaultModel) ||
											!form.defaultModel
												? modelos
												: [{ id: form.defaultModel }, ...modelos]
											).map((m) => (
												<SelectItem key={m.id} value={m.id}>
													{m.label || m.id}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								) : (
									<Input
										id="modeloProvedor"
										placeholder="Busque a lista ou digite o nome"
										value={form.defaultModel || ""}
										onChange={(e) =>
											setForm({ ...form, defaultModel: e.target.value })
										}
									/>
								)}

								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-7 w-full text-xs"
									onClick={() => listarModelos()}
									disabled={buscandoModelos}
								>
									{buscandoModelos ? (
										<Loader2 className="h-3 w-3 animate-spin" />
									) : (
										<RefreshCw className="h-3 w-3" />
									)}
									{modelos.length > 0 ? "Atualizar lista" : "Buscar modelos"}
								</Button>

								<p className="text-xs text-muted-foreground">
									{modelos.length > 0
										? "Os mais recentes aparecem primeiro."
										: "Cole a chave e busque: a lista vem do próprio provedor."}
								</p>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="ordemProvedor">Ordem de uso</Label>
								<Input
									id="ordemProvedor"
									type="number"
									min="1"
									value={form.priority ?? 100}
									onChange={(e) =>
										setForm({ ...form, priority: e.target.value })
									}
								/>
								<p className="text-xs text-muted-foreground">
									Menor = tentado primeiro.
								</p>
							</div>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(null)}>
							Cancelar
						</Button>
						<Button
							disabled={salvando || !form.provider || !form.name}
							onClick={salvarProvedor}
						>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ================= modal da rota ================= */}
			<Dialog open={modal === "rota"} onOpenChange={() => setModal(null)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{form.name}</DialogTitle>
						<DialogDescription>
							Quem atende este recurso e o que fazer se falhar.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label>IA principal</Label>
							<Select
								value={form.aiProviderId}
								onValueChange={(v) => setForm({ ...form, aiProviderId: v })}
							>
								<SelectTrigger>
									<SelectValue placeholder="Primeira disponível" />
								</SelectTrigger>
								<SelectContent>
									{providers.map((p) => (
										<SelectItem key={p.id} value={String(p.id)}>
											{p.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="modeloRota">Modelo</Label>
							<Input
								id="modeloRota"
								placeholder="Deixe vazio para usar o padrão do provedor"
								value={form.model || ""}
								onChange={(e) => setForm({ ...form, model: e.target.value })}
							/>
						</div>

						<div className="space-y-1.5">
							<Label>Se houver mais de uma opção, usar</Label>
							<Select
								value={form.strategy}
								onValueChange={(v) => setForm({ ...form, strategy: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{ESTRATEGIAS.map((e) => (
										<SelectItem key={e.value} value={e.value}>
											{e.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-1.5">
							<Label>Reservas, na ordem</Label>
							<div className="space-y-1 rounded-lg border p-2">
								{providers
									.filter((p) => String(p.id) !== form.aiProviderId)
									.map((p) => {
										const posicao = (form.fallbackChain || []).indexOf(p.id);
										const escolhida = posicao >= 0;

										return (
											<button
												key={p.id}
												type="button"
												onClick={() =>
													setForm({
														...form,
														fallbackChain: escolhida
															? form.fallbackChain.filter((id) => id !== p.id)
															: [...(form.fallbackChain || []), p.id],
													})
												}
												className={cn(
													"flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
													escolhida
														? "bg-primary/10 text-primary"
														: "hover:bg-muted"
												)}
											>
												<span
													className={cn(
														"flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold",
														escolhida && "border-primary bg-primary text-primary-foreground"
													)}
												>
													{escolhida ? posicao + 1 : ""}
												</span>
												<span className="truncate">{p.name}</span>
											</button>
										);
									})}
								{providers.length <= 1 && (
									<p className="px-2 py-3 text-center text-xs text-muted-foreground">
										Conecte outra IA para ter uma reserva.
									</p>
								)}
							</div>
							<p className="text-xs text-muted-foreground">
								Se a principal falhar, o sistema tenta estas nesta ordem.
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(null)}>
							Cancelar
						</Button>
						<Button disabled={salvando} onClick={salvarRota}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default AiHub;
